import React, { useCallback, useEffect, useState } from 'react';
import { HTTPResponse } from '@/class/HTTPResponse';
import { useAuthContext } from '../../context/AuthContext';
import { fetchData } from '../../lib/api';
import {
  adminCardAccent,
  adminMutedText,
  adminSectionTitle,
  adminTable,
  adminTableCell,
  adminTableCellName,
  adminTableHead,
  adminTableWrap,
} from '../layout/adminStyles';

interface SpecialSaleLog {
  id: number;
  item_id: number;
  item_name: string;
  price: number;
  unit: string;
  started_at: string;
}

const formatStartedAtParts = (startedAt: string): { date: string; time: string } => {
  const date = new Date(startedAt);
  if (Number.isNaN(date.getTime())) {
    return { date: startedAt, time: '' };
  }

  return {
    date: date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      timeZone: 'Asia/Tokyo',
    }),
    time: date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Tokyo',
    }),
  };
};

const SpecialSaleLogList: React.FC = () => {
  const { store } = useAuthContext();
  const [logs, setLogs] = useState<SpecialSaleLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadMonthlyLogs = useCallback(async () => {
    if (!store?.id) {
      setLogs([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const res: HTTPResponse = await fetchData(`store/${store.id}/special-sale/monthly`);
      const nextLogs = Array.isArray(res.data?.logs) ? (res.data.logs as SpecialSaleLog[]) : [];
      setLogs(nextLogs);
    } catch (err: unknown) {
      console.error(err);
      setLogs([]);
      setErrorMessage('特売ログの取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, [store?.id]);

  useEffect(() => {
    loadMonthlyLogs();
  }, [loadMonthlyLogs]);

  return (
    <div className={`${adminCardAccent} border-l-red-400 bg-gradient-to-br from-white to-red-50/20`}>
      <h4 className={`${adminSectionTitle} mb-0 border-l-red-400 pb-0`}>
        特売商品ログ（過去31日）
      </h4>
      <p className={`${adminMutedText} mt-2 mb-4 text-sm`}>
        特売に設定した商品の履歴を新しい順に表示します。
      </p>

      {isLoading ? (
        <p className={`${adminMutedText} text-sm`}>読み込み中...</p>
      ) : errorMessage ? (
        <p className={`${adminMutedText} text-sm`}>{errorMessage}</p>
      ) : logs.length === 0 ? (
        <p className={`${adminMutedText} text-sm`}>表示できる特売ログがありません。</p>
      ) : (
        <div className={adminTableWrap}>
          <table className={`${adminTable} !table-auto`}>
            <thead>
              <tr>
                <th className={`${adminTableHead} ${adminTableCell} w-[6.5rem] whitespace-nowrap`}>日時</th>
                <th className={`${adminTableHead} ${adminTableCell}`}>商品名</th>
                <th className={`${adminTableHead} ${adminTableCell} whitespace-nowrap text-right`}>価格</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const startedAt = formatStartedAtParts(log.started_at);

                return (
                <tr key={log.id} className="bg-white even:bg-stone-50/60">
                  <td className={`${adminTableCell} align-top leading-snug`}>
                    <span className="block whitespace-nowrap">{startedAt.date}</span>
                    {startedAt.time ? (
                      <span className="mt-0.5 block whitespace-nowrap text-xs text-stone-500">
                        {startedAt.time}
                      </span>
                    ) : null}
                  </td>
                  <td className={adminTableCellName}>{log.item_name}</td>
                  <td className={`${adminTableCell} whitespace-nowrap text-right font-medium text-stone-800 align-top`}>
                    ¥{log.price.toLocaleString('ja-JP')}/{log.unit}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SpecialSaleLogList;
